import { watch } from 'vue';
import isEqual from 'lodash.isequal';

import {
  applyPendedSubscribers,
  getActualAuthToken,
  resetPendedSubscribers,
} from '@/api/sendApiRequest';
import { TAppStorePlain, TTokenRefreshState } from '@/stores/app/types';
import { EWasAuthBy, MAX_TOKEN_REFRESH_HEARTBEAT_DELAY } from '@/constants';
import { TSessionWithTokensSerialized } from '@/contexts/accountingContext/domain/types';
import { isTokenRefreshHeartbeatStopped } from '@/domains/isTokenRefreshHeartbeatStopped';
import { getMsFromDate } from '@/utils/dateUtils';
import { DEFAULT_CONTRACTOR_ACCOUNT_PATH } from '@/contexts/contractorsContext/domain/constants';
import { useAppStore } from '@/stores/app';
import { useWebsocketController } from '@/composables/useWebsocketController';

/** Композибл useSyncTokens необходим для корректной синхронизации токенов между вкладками одного браузера.
 * Автоматически vuex/pinia не синхронизируют сторы между активными и неактивными вкладками одного и того же домена,
 * что приводит к ситуации, когда токен в одной вкладке обновился, а вторая вкладка этого не узнала и тоже обновила сама себе токен.
 * Такое поведение приводит к разлогинам пользователей, потому что к запросам цепляется удаленный токен из базы, а не свежий из соседней вкладки.
 *
 * Для решения этой проблемы необходимо отслеживать событие 'storage', которое срабатывает только в неактивных вкладках, когда в активной что-то поменялось в сторе.
 *
 * Так же useSyncTokens используется для реализации механизма, отслеживающего попытки одновременного обновления токена из нескольких вкладок.
 * Данный механизм позволяет выбрать только одну вкладку, которая будет заниматься обновлением. Остальные вкладки переодически проверяют, обновился ли токен или нет, а так же
 * в случае если вкладка, обновляющая токен, не отвечает, могут перехватить обновление токена.
 *
 * Состояние обновления токена устанавливается в getActualAuthToken с помощью SET_TOKEN_REFRESH_STATE.
 */
export const useSyncTokens = () => {
  const appStore = useAppStore();

  let refreshAuthTokenTimeoutId: ReturnType<typeof setTimeout>;
  /** Текущая ли вкладка обновляла токен */
  let isTokenRefreshedByCurrentTab = false;

  const { checkWebsocketConnection } = useWebsocketController();

  /**
   * Переменная для хранения предыдущего состояния обновления токена (с isRefreshing=true).
   *
   * Используется для отсеивания лишних событий storage, содержащих уже не актуальный статус обновления.
   * К примеру, вкладка, которая обновляла токен, после успешного обновления 2 раза изменяет LS, первый раз когда устанавливает новый токен, второй раз, когда сбрасывает состояние обновления.
   * Остальные вкладки перехватив первое событие storage (с новым токеном), обновят свой LS и возникнет новое событие storage, с новым ключем, но уже с неактуальным tokenRefreshState (не успел во вкладках сброситься).
   * Такое поведение может привести к зацикливанию.
   */
  let previousRefreshState: TTokenRefreshState | null = null;

  /**
   * Функция вызывается (когда запущен механизм обновления токена) во вкладках, кроме той, в которой производится обновление токена.
   *
   * Основная задача - перехватить обновление токена, если вкладка, обновляющая токен, не отвечает (была закрыта, обновили страницу).
   * Если процесс обновления активен, функция перезапустить сама себя через заданный промежуток, для повторной проверки условия.
   */
  const refreshAuthToken = () => {
    if (!appStore.tokenRefreshState.isRefreshing) return;

    // если вкладка, обновляющая токен, не отвечает (была закрыта, обновили страницу)
    if (isTokenRefreshHeartbeatStopped(appStore.tokenRefreshState.heartbeatAt)) {
      getActualAuthToken()
        .catch(() => {});
    // иначе вызываем повторно данную функцию через заданный промежуток
    } else {
      refreshAuthTokenTimeoutId = setTimeout(refreshAuthToken, MAX_TOKEN_REFRESH_HEARTBEAT_DELAY);
    }
  };

  const getTokenData = (appState: TAppStorePlain): TSessionWithTokensSerialized => ({
    accessToken: appState.accessToken || '',
    accessTokenExpiresAt: appState.accessTokenExpiresAt || '',
    refreshToken: appState.refreshToken || '',
    refreshTokenExpiresAt: appState.refreshTokenExpiresAt || '',
    csrfToken: appState.csrfToken || '',
  });

  const logout = (newAppState: TAppStorePlain, currentWasAuthBy: EWasAuthBy | null) => {
    // Если в другой вкладке происходит вход в ЛК сотрудника с временным доступом или выход из данного ЛК (возвращаемся обратно в ЛК администратора)
    if (newAppState.isTemporaryAccessSessionSwitching) return;

    appStore.setAuthTokens({
      accessToken: '',
      accessTokenExpiresAt: '',
      refreshToken: '',
      refreshTokenExpiresAt: '',
      csrfToken: '',
    });

    // если были в ЛК контрагента и вышли, делаем сброс, иначе будет кидать на страницу с авторизацией в ЛК контрагента
    if (currentWasAuthBy === EWasAuthBy.entryCode) {
      appStore.setWasAuthBy(EWasAuthBy.loginPassword);
      appStore.setRouteBeforeLogout(null);
    }

    appStore.$reset();
  };

  const login = (newAppState: TAppStorePlain) => {
    appStore.setAuthTokens(getTokenData(newAppState));
    window.location.reload();
  };

  const isSwitchingProcessToTemporaryAccessAccount = (
    currentWasAuthBy: EWasAuthBy | null,
    newWasAuthBy: EWasAuthBy | null,
  ) => currentWasAuthBy !== EWasAuthBy.temporaryAccessSession && newWasAuthBy === EWasAuthBy.temporaryAccessSession;

  const isSwitchingProcessFromTemporaryAccessAccount = (
    currentWasAuthBy: EWasAuthBy | null,
    newWasAuthBy: EWasAuthBy | null,
  ) => currentWasAuthBy === EWasAuthBy.temporaryAccessSession && newWasAuthBy !== EWasAuthBy.temporaryAccessSession;

  const swapToken = (newAppState: TAppStorePlain, currentAppState: TAppStorePlain) => {
    // Проверяем был ли переход в ЛК сотрудника с временным доступом или из данного ЛК
    if (isSwitchingProcessToTemporaryAccessAccount(currentAppState.wasAuthBy, newAppState.wasAuthBy)
      || isSwitchingProcessFromTemporaryAccessAccount(currentAppState.wasAuthBy, newAppState.wasAuthBy)
    ) {
      window.location.reload();

      return;
    }

    const tokenData = getTokenData(newAppState);

    // Проверяем был ли переход в ЛК контрагента
    if (currentAppState.wasAuthBy !== EWasAuthBy.entryCode && newAppState.wasAuthBy === EWasAuthBy.entryCode) {
      appStore.setWasAuthBy(EWasAuthBy.entryCode);
      appStore.setAuthTokens(tokenData);
      window.location.replace(DEFAULT_CONTRACTOR_ACCOUNT_PATH);
      return;
    }

    const currentAccessTokenExpiresAtMs = currentAppState.accessTokenExpiresAt
      ? getMsFromDate(currentAppState.accessTokenExpiresAt)
      : 0;

    const newAccessTokenExpiresAtMs = newAppState.accessTokenExpiresAt
      ? getMsFromDate(newAppState.accessTokenExpiresAt)
      : 0;

    /**
      * Если время жизни токена из события storage меньше текущего установленного.
      *
      * Отсеиваем события storage со старым токеном, от вкладок, которые еще не успели
      * изменить токен на новый, но стали причиной появления событие storage
      * (изменился, к примеру, параметр tokenRefreshState).
    */
    if (newAccessTokenExpiresAtMs < currentAccessTokenExpiresAtMs) return;
    appStore.setAuthTokens(tokenData);
  };

  const syncTokens = (event: StorageEvent) => {
    if (!event.newValue) return;
    const newAppState = JSON.parse(event.newValue);

    if (!newAppState) return;

    const { accessToken: currentAccessToken, wasAuthBy } = appStore;

    // В одной из вкладок logout или переключение между ЛК (входим или выходим в ЛК с временным доступом)
    if (!newAppState.accessToken && currentAccessToken) {
      logout(newAppState, wasAuthBy);
    // В одной из вкладок login
    } else if (!currentAccessToken && newAppState.accessToken) {
      login(newAppState);
    // смена токена с одного на другой (оба не null)
    } else if (newAppState.accessToken !== currentAccessToken) {
      swapToken(newAppState, appStore);
    }
  };

  const syncTokenRefreshState = (event: StorageEvent) => {
    if (!event.newValue) return;
    const { tokenRefreshState: newRefreshState } = JSON.parse(event.newValue) || {};

    const {
      currentTabId,
      tokenRefreshState: currentRefreshState,
    } = appStore;

    /** Если текущая вкладка запустила обновление (от других вкладок может прилететь событие storage обратно) */
    if (!newRefreshState || newRefreshState.tabId === currentTabId) return;

    if (isEqual(newRefreshState, previousRefreshState) || isEqual(newRefreshState, currentRefreshState)) return;

    /**
     * Ниже проверяется ряд условий, чтобы отсеить лишние события storage.
     * Событий может быть много, так как несколько вкладок могут параллельно (одновременно или с небольшой разницей во времени) пойти обновлять токен.
     * И нужно будет решить, кому отдать право на обновление.
    */
    if (newRefreshState.tabId && currentRefreshState.tabId && newRefreshState.tabId !== currentRefreshState.tabId) {
      const diff = (newRefreshState.startedAt || 0) - (currentRefreshState.startedAt || 0);
      const heartbeatAt = currentRefreshState.heartbeatAt || 0;

      /**
      * Если несколько вкладок одновременно пытаются обновить токен, сравниваем их id (приоритет отдается вкладке с меньшим id).
      *
      * ИЛИ если вкладка обновляющая токен жива
      *
      * выходим (не перезаписываем стейт)
     */
      if ((!diff && (newRefreshState.tabId.localeCompare(currentRefreshState.tabId) > 0))
        || (diff > 0 && !isTokenRefreshHeartbeatStopped(heartbeatAt))) return;
    }

    if (newRefreshState.isRefreshing) {
      previousRefreshState = newRefreshState;
    }

    appStore.setTokenRefreshState(newRefreshState);
  };

  const syncRefreshTokensFromLsToStore = (event: StorageEvent) => {
    if (event.key !== 'TMS_APP') return;
    syncTokens(event);
    syncTokenRefreshState(event);
  };

  watch(() => appStore.tokenRefreshState, (newState: TTokenRefreshState, oldState?: TTokenRefreshState) => {
    if (!newState.isRefreshing) {
      if (oldState) {
        clearTimeout(refreshAuthTokenTimeoutId);
      }
      return;
    }
    if (!appStore.accessToken) {
      appStore.resetRefreshingProcess();
      return;
    }
    isTokenRefreshedByCurrentTab = appStore.isCurrentTabRefreshingToken;

    // Если процесс обновления был запущен ранее (новое состояние может появиться при изменении heartbeat) или обновление было запущено текущей вкладкой
    if (newState.tabId === oldState?.tabId || appStore.isCurrentTabRefreshingToken) return;

    clearTimeout(refreshAuthTokenTimeoutId);
    refreshAuthToken();
  }, { immediate: true });

  watch(() => appStore.accessToken, (newAccessToken: string | null, oldAccessToken: string | null) => {
    checkWebsocketConnection(newAccessToken, oldAccessToken);
    if (isTokenRefreshedByCurrentTab) return;

    if (newAccessToken && oldAccessToken && newAccessToken !== oldAccessToken) {
      // запускаем выполнение запросов, ожидающих токен
      applyPendedSubscribers({
        accessToken: appStore.accessToken,
        accessTokenExpiresAt: appStore.accessTokenExpiresAt,
        csrfToken: appStore.csrfToken,
        refreshToken: appStore.refreshToken,
        refreshTokenExpiresAt: appStore.refreshTokenExpiresAt,
      });
    } else if (!newAccessToken) {
      resetPendedSubscribers();
    }
  });

  return { syncRefreshTokensFromLsToStore };
};
